import { Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { trackDemoClick, trackTrialClick } from "@/lib/analytics";
import { useState, useRef, useEffect } from "react";
import logo from "@/assets/complyhub-logo.svg";

type Cta = { 
  label: string; 
  href: string; 
  variant?: "primary" | "secondary";
  trackingId?: string;
};

export interface SiteHeroProps {
  eyebrow?: string;
  title: string;
  subtitle?: string;
  riskLine?: string;
  standardsLine?: string;
  bullets?: string[];
  primaryCta?: Cta;
  secondaryCta?: Cta;

  // right side media
  videoSrc?: string;
  videoType?: "file" | "vimeo";
  imageSrc?: string;
  poster?: string;
  mediaLabel?: string;
  mediaAlt?: string;
  mediaCaption?: string;
  videoAspect?: "9:16" | "16:9";

  // optional trust strip below
  trustText?: string;
  trustCta?: Cta;

  // styling toggles
  showWave?: boolean;
  nextSectionFill?: string;
  mediaMaxWidth?: number;

  // social proof row overrides
  socialProofText?: string;
  showSocialAvatars?: boolean;
}

// Vivacity brand dot texture overlay
const dotPattern =
  "radial-gradient(circle, rgba(255,255,255,0.08) 1.5px, transparent 1.5px)";

/**
 * SiteHero - Unified hero component for all marketing pages
 * Features: 2/3 content to 1/3 media layout, gradient background with compliance/AI pattern,
 * wave divider, eyebrow pill, bullets, CTAs, optional trust strip
 */
export default function SiteHero({
  eyebrow,
  title,
  subtitle,
  riskLine,
  standardsLine,
  bullets,
  primaryCta,
  secondaryCta,
  videoSrc,
  videoType = "file",
  imageSrc,
  poster,
  mediaLabel = "Live preview",
  mediaAlt,
  mediaCaption,
  videoAspect = "9:16",
  trustText,
  trustCta,
  showWave = true,
  nextSectionFill = "#FFFFFF",
  mediaMaxWidth = 270,
  socialProofText,
  showSocialAvatars = true,
}: SiteHeroProps) {
  const hasMedia = videoSrc || imageSrc;
  
  // Lazy load Vimeo iframes to reduce initial JS execution time
  const [loadVimeo, setLoadVimeo] = useState(false);
  const mediaRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    if (!videoSrc || videoType !== "vimeo") return;
    
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setLoadVimeo(true);
          observer.disconnect();
        }
      },
      { rootMargin: "100px" }
    );
    
    if (mediaRef.current) {
      observer.observe(mediaRef.current);
    }
    
    return () => observer.disconnect();
  }, [videoSrc, videoType]);

  const handlePrimaryClick = () => {
    if (primaryCta?.trackingId) {
      if (primaryCta.href.includes("trial")) {
        trackTrialClick(primaryCta.trackingId);
      } else {
        trackDemoClick(primaryCta.trackingId);
      }
    }
  };

  const handleSecondaryClick = () => {
    if (secondaryCta?.trackingId) {
      if (secondaryCta.href.includes("trial")) {
        trackTrialClick(secondaryCta.trackingId);
      } else {
        trackDemoClick(secondaryCta.trackingId);
      }
    }
  };

  const handleTrustCtaClick = () => {
    if (trustCta?.trackingId) {
      if (trustCta.href.includes("trial")) {
        trackTrialClick(trustCta.trackingId);
      } else {
        trackDemoClick(trustCta.trackingId);
      }
    }
  };

  return (
    <section className="relative overflow-hidden flex items-start md:h-[760px]">
      {/* Vivacity brand gradient background */}
      <div
        className="absolute inset-0"
        aria-hidden="true"
        style={{
          background:
            "linear-gradient(135deg, #7130A0 0%, #44235F 30%, #ED1878 80%, #C81069 100%)",
        }}
      />

      {/* Dot texture overlay */}
      <div
        className="absolute inset-0 pointer-events-none"
        aria-hidden="true"
        style={{
          backgroundImage: dotPattern,
          backgroundSize: "20px 20px",
        }}
      />

      <div className="relative w-full h-full mx-auto max-w-7xl px-6 lg:px-20 pt-[calc(var(--banner-offset,0px)+176px)] pb-[92px] lg:pb-[135px]">
        <div
          className={`grid items-start h-full gap-12 ${hasMedia ? "lg:grid-cols-[1fr_var(--hero-media-w)]" : ""}`}
          style={hasMedia ? ({ ["--hero-media-w" as any]: `${mediaMaxWidth}px` }) : undefined}
        >
          {/* Left content */}
          <div className={`${hasMedia ? "" : "max-w-3xl mx-auto text-center"} text-white animate-fade-up pt-0 pb-[20px]`}>
            <div
              className={`inline-flex items-center gap-2 rounded-full bg-white/15 text-white ${eyebrow ? "" : "invisible"}`}
              style={{ padding: "4px 14px", fontSize: 13, marginBottom: 16 }}
              aria-hidden={!eyebrow}
            >
              <Calendar className="h-3.5 w-3.5" />
              {eyebrow || "Hero overview"}
            </div>

            <h1 className="font-heading text-white tracking-hero" style={{ fontSize: "clamp(2.3125rem, 5vw, 3.25rem)", fontWeight: 800, lineHeight: 1.1, marginBottom: 20 }}>
              {title}
            </h1>

            {subtitle && (
              <p className="max-w-2xl" style={{ fontFamily: "Calibri, sans-serif", fontSize: 18, lineHeight: 1.6, color: "rgba(255,255,255,0.9)", marginBottom: 32 }}>
                {subtitle}
              </p>
            )}

            {/* riskLine, standardsLine, bullets removed */}

            {(primaryCta || secondaryCta) && (
              <div className={`flex flex-col gap-4 sm:flex-row sm:items-center ${!hasMedia ? "justify-center" : ""}`} style={{ marginBottom: 24 }}>
                {primaryCta && (
                  <Button
                    size="lg"
                    variant="pill-primary"
                    className="text-base h-auto py-3.5"
                    asChild
                  >
                    <a
                      href={primaryCta.href}
                      target={primaryCta.href.startsWith("http") ? "_blank" : undefined}
                      rel={primaryCta.href.startsWith("http") ? "noopener noreferrer" : undefined}
                      onClick={handlePrimaryClick}
                    >
                      {primaryCta.label}
                    </a>
                  </Button>
                )}
                {secondaryCta && (
                  <Button
                    size="lg"
                    variant="pill-ghost"
                    className="text-base h-auto py-3.5"
                    asChild
                  >
                    <a
                      href={secondaryCta.href}
                      target={secondaryCta.href.startsWith("http") ? "_blank" : undefined}
                      rel={secondaryCta.href.startsWith("http") ? "noopener noreferrer" : undefined}
                      onClick={handleSecondaryClick}
                    >
                      {secondaryCta.label}
                    </a>
                  </Button>
                )}
              </div>
            )}

            {/* Social proof row */}
            {showSocialAvatars !== false && (
            <a
              href="/executive-summary"
              className="social-proof-pill group inline-flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-3xl bg-white/10 hover:bg-white/15 px-3 py-1.5 max-w-full"
              style={{ marginTop: 8 }}
              aria-label="See which RTOs are using ComplyHub"
            >
              {showSocialAvatars && (
                <div className="flex -space-x-2 shrink-0">
                  {["SM", "DC", "ER", "JT"].map((initials, i) => {
                    const bg = ["#7130A0", "#23C0DD", "#ED1878", "#44235F"][i];
                    return (
                      <div
                        key={initials}
                        className="w-7 h-7 rounded-full border-2 border-white/40 flex items-center justify-center text-[10px] font-bold text-white"
                        style={{ background: bg }}
                      >
                        {initials}
                      </div>
                    );
                  })}
                </div>
              )}
              <span className="min-w-0" style={{ fontSize: 14, color: "rgba(255,255,255,0.9)" }}>
                {socialProofText ?? "Join 24 RTOs already audit-ready"}
              </span>
              <span
                className="sp-reveal"
                style={{ fontSize: 13, color: "#23C0DD", fontWeight: 600 }}
              >
                See who’s using ComplyHub →
              </span>
            </a>
            )}
          </div>

          {/* Right media card — fixed shared footprint */}
          {hasMedia && (
            <div className="animate-hero-card-in flex justify-center lg:justify-end w-full mt-0 mb-[20px]">
              <div 
                ref={mediaRef}
                className="hero-media-card group relative overflow-hidden shadow-2xl bg-[#DFD8E8] w-full lg:max-w-none"
                style={{
                  borderRadius: "var(--radius-hero-card, 16px)",
                  flexShrink: 0,
                  height: "460px",
                  maxHeight: "460px",
                  maxWidth: `${mediaMaxWidth}px`,
                  width: `${mediaMaxWidth}px`,
                }}
              >
                {videoSrc && videoType === "vimeo" ? (
                    loadVimeo ? (
                      <iframe
                        className="h-full w-full object-cover"
                        src={videoSrc}
                        frameBorder="0"
                        allow="autoplay; fullscreen; picture-in-picture"
                        allowFullScreen
                        title="Hero video"
                        loading="lazy"
                      />
                    ) : (
                      <div className="ch-skeleton h-full w-full" />
                    )
                  ) : videoSrc ? (
                    <video
                      className="h-full w-full object-cover"
                      src={videoSrc}
                      poster={poster}
                      autoPlay
                      muted
                      loop
                      playsInline
                      preload="metadata"
                    />
                  ) : imageSrc ? (
                    <img
                      className="w-full h-full object-cover object-center block"
                      src={imageSrc}
                      alt={mediaAlt ?? mediaLabel ?? ""}
                      fetchPriority="high"
                      decoding="async"
                      onError={(e) => {
                        const img = e.currentTarget;
                        img.style.display = "none";
                        const parent = img.parentElement;
                        if (parent && !parent.querySelector('[data-hero-fallback]')) {
                          const fb = document.createElement('div');
                          fb.setAttribute('data-hero-fallback', 'true');
                          fb.className = 'absolute inset-0 flex items-center justify-center bg-[#DFD8E8]';
                          fb.innerHTML = `<img src="${logo}" alt="ComplyHub Platform" style="width:60%;max-width:220px;opacity:0.85" />`;
                          parent.appendChild(fb);
                        }
                      }}
                    />
                  ) : null}

                {/* Badge label top-left */}
                {mediaLabel && (
                  <div className="absolute text-white" style={{ top: 12, left: 12, background: "rgba(0,0,0,0.45)", borderRadius: 6, padding: "6px 10px", fontSize: 12 }}>
                    {mediaLabel}
                  </div>
                )}

                {/* Caption bar bottom */}
                {mediaCaption && (
                  <div
                    className="hero-caption absolute bottom-0 left-0 right-0 text-white"
                    style={{ background: "rgba(0,0,0,0.55)", padding: "12px 16px", fontSize: 13 }}
                  >
                    <div>{mediaCaption}</div>
                    <div className="hero-caption-link mt-1" style={{ color: "#23C0DD", fontWeight: 600 }}>
                      See it in action →
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

      </div>

      {/* Wave divider to next section */}
      {showWave && (
        <div className="absolute bottom-0 left-0 right-0 w-full" aria-hidden="true">
          <svg 
            viewBox="0 0 1440 120" 
            className="h-[80px] w-full block"
            preserveAspectRatio="none"
          >
            <path
              fill={nextSectionFill}
              d="M0,40 C360,80 720,0 1080,60 C1260,90 1380,70 1440,50 L1440,120 L0,120 Z"
            />
          </svg>
        </div>
      )}
    </section>
  );
}
